/**
 * ResetPasswordDialog — Centered modal dialog for resetting a user's password.
 *
 * Opens from the row-level "Reset Password" action in UsersTable.
 * Collects new password + confirmation, validates, then delegates to the
 * orchestrator's handleResetPassword callback.
 *
 * @param {{
 *   user: object,
 *   onClose: () => void,
 *   onConfirm: (data: { user_id, username, password }) => Promise<[boolean, string?]>,
 *   roleData: Array<{ user_role_id: number, user_role_name: string }>,
 * }} props
 */

import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import {
  PASSWORD_MIN_LENGTH, PASSWORD_PATTERN_ATTR, PASSWORD_RULE_HINT, ADMIN_WEAK_PASSWORD_MSG,
  isPasswordFormatValid, isAdminWeakPassword as isWeakForRole,
} from '../../../utils/passwordRules.js';

const ResetPasswordDialog = ({ user, onClose, onConfirm, roleData }) => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [apiError, setApiError] = useState('');
  const inputRef = useRef(null);

  // Focus the new password field on mount
  useEffect(() => {
    const timer = setTimeout(() => inputRef.current?.focus(), 150);
    return () => clearTimeout(timer);
  }, []);

  // Close on Escape
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  // Validation
  const passwordFormatBad = newPassword.length > 0 && !isPasswordFormatValid(newPassword);
  const passwordsMismatch = confirmPassword.length > 0 && newPassword !== confirmPassword;
  const isAdminWeakPassword = isWeakForRole(newPassword, user?.user_role_id);

  const isValid = useMemo(() => {
    if (!isPasswordFormatValid(newPassword)) return false;
    if (newPassword !== confirmPassword) return false;
    if (isAdminWeakPassword) return false;
    return true;
  }, [newPassword, confirmPassword, isAdminWeakPassword]);

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      if (!isValid || saving) return;
      setSaving(true);
      setApiError('');

      try {
        const result = await onConfirm({
          user_id: user.user_id,
          username: user.username,
          password: newPassword,
        });
        if (!result[0]) {
          setApiError(result[1] || 'Password reset failed. Please try again.');
          setSaving(false);
        }
        // On success the orchestrator will close the dialog & show toast
      } catch {
        setApiError('An unexpected error occurred.');
        setSaving(false);
      }
    },
    [isValid, saving, user, newPassword, onConfirm]
  );

  return (
    <div className="pp-dialog-backdrop" onClick={onClose}>
      <div
        className="pp-dialog pp-dialog--sm"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Reset Password"
      >
        {/* Header */}
        <div className="pp-dialog__header pp-dialog__header--top">
          <div>
            <h2 className="pp-dialog__title">Reset Password</h2>
            <p className="pp-dialog__subtitle">
              {user?.display_name || user?.username}
              {user?.display_name && user?.username ? (
                <span className="up-dialog__username"> ({user.username})</span>
              ) : null}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="up-drawer__close"
            aria-label="Close dialog"
          >
            <svg className="up-icon-lg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit}>
          <div className="pp-dialog__body pp-dialog__body--stack">
            {apiError ? (
              <div className="pp-dialog__error-banner">
                <svg className="up-icon-sm" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
                </svg>
                <span>{apiError}</span>
              </div>
            ) : null}

            <div>
              <label htmlFor="reset-new-password" className="up-field__label">
                New Password <span className="up-field__required">*</span>
              </label>
              <input
                ref={inputRef}
                type="password"
                id="reset-new-password"
                value={newPassword}
                onChange={(e) => { setNewPassword(e.target.value); setApiError(''); }}
                className="up-field__input"
                placeholder="Minimum 6 characters, letters and numbers only"
                autoComplete="new-password"
                disabled={saving}
                minLength={PASSWORD_MIN_LENGTH}
                pattern={PASSWORD_PATTERN_ATTR}
                title={PASSWORD_RULE_HINT}
              />
              {passwordFormatBad ? (
                <p className="up-field__error">{PASSWORD_RULE_HINT}</p>
              ) : (
                <p className="up-field__hint">{PASSWORD_RULE_HINT}</p>
              )}
              {isAdminWeakPassword ? (
                <p className="up-field__error">{ADMIN_WEAK_PASSWORD_MSG}</p>
              ) : null}
            </div>

            <div>
              <label htmlFor="reset-confirm-password" className="up-field__label">
                Confirm Password <span className="up-field__required">*</span>
              </label>
              <input
                type="password"
                id="reset-confirm-password"
                value={confirmPassword}
                onChange={(e) => { setConfirmPassword(e.target.value); setApiError(''); }}
                className="up-field__input"
                placeholder="Re-enter new password"
                autoComplete="new-password"
                disabled={saving}
              />
              {passwordsMismatch ? (
                <p className="up-field__error">Passwords do not match.</p>
              ) : null}
            </div>
          </div>

          {/* Footer */}
          <div className="pp-dialog__footer">
            <button
              type="button"
              onClick={onClose}
              className="up-btn up-btn--cancel"
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!isValid || saving}
              className="up-btn up-btn--primary"
            >
              {saving ? (
                <span className="up-saving-text">
                  <svg className="up-icon-md up-spinner" fill="none" viewBox="0 0 24 24">
                    <circle style={{ opacity: 0.25 }} cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path style={{ opacity: 0.75 }} fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Resetting...
                </span>
              ) : (
                'Reset Password'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ResetPasswordDialog;
